import { useState, useRef, useEffect } from "react";
import { FaChevronDown, FaPlus } from "react-icons/fa";
import CreateTagModal from "./CreateTagModal";

const TagDropdown = ({
  tags,
  selectedTags = [],
  onTagsChange,
  onTagCreate,
  placeholder = "Filter by tags...",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleTagToggle = (tag) => {
    const isSelected = selectedTags.some(
      (selectedTag) => selectedTag.id === tag.id
    );
    let newSelectedTags;

    if (isSelected) {
      newSelectedTags = selectedTags.filter(
        (selectedTag) => selectedTag.id !== tag.id
      );
    } else {
      newSelectedTags = [...selectedTags, tag];
    }

    onTagsChange(newSelectedTags);
  };

  const handleRemoveTag = (tagToRemove) => {
    const newSelectedTags = selectedTags.filter(
      (tag) => tag.id !== tagToRemove.id
    );
    onTagsChange(newSelectedTags);
  };

  const clearAllTags = () => {
    onTagsChange([]);
  };

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      <div className="flex flex-col space-y-2">
        <div className="flex justify-end" ref={dropdownRef}>
          <button
            onClick={handleClick}
            className="flex items-center justify-around w-48 px-3 py-2 text-left bg-[#254884] text-gray-200 rounded-sm shadow-sm hover:bg-[#1e3a6a] focus:outline-none focus:ring-1 focus:ring-gray-200"
          >
            <span className="truncate">
              {selectedTags.length === 0
                ? placeholder
                : `${selectedTags.length} tag${
                    selectedTags.length !== 1 ? "s" : ""
                  } selected`}
            </span>
            <FaChevronDown />
          </button>

          {isOpen && (
            <div className="absolute z-50 w-48 mt-10 bg-[#254884] border border-gray-300 rounded-sm shadow-lg right-60">
              <div className="max-h-48 overflow-y-auto">
                {selectedTags.length > 0 && (
                  <div className="p-2 border-b border-gray-200">
                    <button
                      onClick={clearAllTags}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Clear all tags
                    </button>
                  </div>
                )}

                {tags.length === 0 ? (
                  <div className="p-3 text-sm text-gray-500">
                    No tags available
                  </div>
                ) : (
                  tags.map((tag) => {
                    const isSelected = selectedTags.some(
                      (selectedTag) => selectedTag.id === tag.id
                    );
                    return (
                      <label
                        key={tag.id}
                        className="flex items-center px-3 py-2 text-gray-200 hover:bg-[#1e3a6a] cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleTagToggle(tag)}
                          className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="text-sm">{tag.name}</span>
                      </label>
                    );
                  })
                )}
              </div>

              {/* Create Tag Button */}
              <div className="p-2 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowCreateModal(true);
                    setIsOpen(false);
                  }}
                  className="flex items-center w-full px-2 py-1 text-sm text-green-400 hover:text-green-300 hover:bg-[#1e3a6a] rounded"
                >
                  <FaPlus className="mr-2 w-3 h-3" />
                  Create New Tag
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Selected tags display below dropdown */}
        {selectedTags.length > 0 && (
          <div className="flex flex-wrap gap-1 max-w-md">
            {selectedTags.map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
              >
                {tag.name}
                <button
                  onClick={() => handleRemoveTag(tag)}
                  className="ml-1 text-blue-600 hover:text-blue-800 focus:outline-none"
                >
                  <svg
                    className="w-3 h-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Create Tag Modal */}
      <CreateTagModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        handleCreateTag={onTagCreate}
      />
    </>
  );
};

export default TagDropdown;
